import { useId, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatTime, itemReason, planLine, setLine, whyLines } from "./lib/format";
import { doneLine } from "./lib/history";

/**
 * One exercise of a past session (History): what the plan asked — with
 * its reason, and on demand the reason for each number — against the sets
 * actually logged, each one tap from being changed (or deleted) in the
 * logging sheet. An exercise logged off the plan says so.
 *
 * Presentational: the session page owns the data and the sheet.
 */
function HistoryExercise({ groupId, name, item = null, exercise, sets, units, planUnits, offPlan = false, timeZone, onEditSet }) {
  const id = useId();
  const [whyOpen, setWhyOpen] = useState(false);
  const role = item?.role ?? exercise?.role;
  // the title says the name; Why doesn't say the reason again (W8)
  const reason = itemReason(item?.reason, name);
  const why = whyLines(item, reason);

  return (
    <li data-group={groupId} className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {/* Focus lands here when the set that was focused is deleted. */}
        <h3
          tabIndex={-1}
          data-heading
          className="m-0 rounded-sm font-display text-lg font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
          {name}
        </h3>
        {role === "main" && item?.day_type && (
          <>
            {" "}
            <Badge variant="secondary">{item.day_type === "heavy" ? "Heavy day" : "Volume day"}</Badge>
          </>
        )}
        {role === "shoulder_health" && (
          <>
            {" "}
            <Badge variant="secondary">Shoulder health</Badge>
          </>
        )}
        {item?.recovery && (
          <>
            {" "}
            <Badge variant="warning">Recovery</Badge>
          </>
        )}
        {/* the exercise's own break, or its muscle's (W10) */}
        {(item?.eased || item?.easing) && (
          <>
            {" "}
            <Badge variant="warning">Easing back in</Badge>
          </>
        )}
        {offPlan && (
          <>
            {" "}
            <Badge variant="outline">Not in the plan</Badge>
          </>
        )}
      </div>

      {item && (
        <p className="mt-1 mb-0 tabular-nums">
          <span className="text-muted-foreground">Planned:</span>{" "}
          <span className="font-medium">{planLine(item, planUnits, exercise)}</span>
        </p>
      )}
      {reason && <p className="mt-1 mb-0 text-sm text-muted-foreground">{reason}</p>}
      {why.length > 0 && (
        <>
          <Button
            type="button"
            variant="ghost"
            className="mt-1 -ml-3 h-11 px-3 text-primary md:h-8"
            aria-expanded={whyOpen}
            aria-controls={`${id}-why`}
            onClick={() => setWhyOpen(!whyOpen)}
          >
            <ChevronDown aria-hidden="true" className={cn("transition-transform", whyOpen && "rotate-180")} />
            Why these numbers{" "}
            <span className="sr-only">for {name}</span>
          </Button>
          {whyOpen && (
            <dl id={`${id}-why`} className="m-0 grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
              {why.map(({ key, label, text }) => (
                <div key={key} className="contents">
                  <dt className="font-medium">{label}</dt>
                  <dd className="m-0 mb-1 text-muted-foreground sm:mb-0">{text}</dd>
                </div>
              ))}
            </dl>
          )}
        </>
      )}

      <p className="mt-3 mb-1 text-sm text-muted-foreground">{doneLine(item, sets.length)}</p>
      {sets.length > 0 && (
        <ol aria-label={`Sets of ${name}`} className="m-0 flex list-none flex-col gap-0.5 p-0">
          {sets.map((set, i) => {
            const line = setLine(set, units, exercise);
            const by = set.source && set.source !== "ui" ? set.logged_by?.username : null;
            const time = formatTime(set.performed_at, timeZone);
            return (
              <li key={set.id}>
                {/* Named by its content, not an aria-label: a label replaced
                    the visible "by scout" and the time for a screen reader
                    (WCAG 2.5.3). The sr-only lead-in says what a tap does;
                    the commas are spoken pauses, out of the flex flow. */}
                <button
                  type="button"
                  data-set-row
                  className="flex min-h-11 w-full items-center gap-3 rounded-md px-2 text-left transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none md:min-h-9"
                  onClick={() => onEditSet(set, i + 1)}
                >
                  <span aria-hidden="true" className="w-5 shrink-0 text-sm text-muted-foreground tabular-nums">
                    {i + 1}
                  </span>
                  <span className="sr-only">{`Edit set ${i + 1} of ${name}:`}</span>{" "}
                  {/* The badge wraps under the set on a phone rather than
                      squeezing it into a column. */}
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 py-1">
                    <span className="tabular-nums">{line}</span>
                    {by && (
                      <>
                        <span className="sr-only">,</span>{" "}
                        <Badge variant="outline">by {by}</Badge>
                      </>
                    )}
                  </span>
                  {time && (
                    <>
                      <span className="sr-only">,</span>{" "}
                      <time dateTime={set.performed_at} className="shrink-0 text-xs text-muted-foreground tabular-nums">
                        {time}
                      </time>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
}

HistoryExercise.propTypes = {
  /** Marks the group, so focus can find it after a set is deleted. */
  groupId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  /** Its plan item (TrainingPlanItem); null for an exercise logged off the plan. */
  item: PropTypes.object,
  /** The catalog's exercise (TrainingExercise), when the catalog has it. */
  exercise: PropTypes.object,
  /** Its logged sets (TrainingSet), oldest first. */
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The session's units (what the sets are in). */
  units: PropTypes.string.isRequired,
  /** The plan's units (what its loads are in). */
  planUnits: PropTypes.string.isRequired,
  /** Logged, though the session had a plan without it. */
  offPlan: PropTypes.bool,
  /** The settings' time zone, for each set's time. */
  timeZone: PropTypes.string,
  /** `onEditSet(set, number)` opens the logging sheet on it. */
  onEditSet: PropTypes.func.isRequired,
};

export default HistoryExercise;
